import { UserPastPurchase, UserProfile } from 'models/user.model'
import { handleError } from 'utils/error'
import axios from '../base'
import { AxiosError } from 'axios'
import {
  ChangePasswordRequest,
  ChildSummary,
  CreateChildRequest,
  CreateChildResponse,
  DishoutSaveCardResponse,
  EditChildRequest,
  GetChildrenResponse,
  UpdateProfileRequest,
  UpdateProfileResponse,
  UserBalanceRequest,
  UserBalanceResponse,
  UserFamilyRequest,
  RemoveUserFamilyRequest,
  UserTransactionsByLocationRequest,
  UserTransactionsByLocationResponse,
} from './user.types'
import { Appointment } from 'services/appointment'

// The DishOut save-card endpoint's exact envelope is TBD-at-staging
// (acespace-backend#123 punch list — HTML field name + casing). Parse
// defensively: accept a top-level or `data`-nested object and the likely
// HTML field names so we're not blocked on one guess.
type RawDishoutSaveCard = {
  html?: string
  form_html?: string
  embed?: string
  state_jwt?: string
  error?: boolean
  description?: string
  data?: RawDishoutSaveCard
}

// product#379 returns real 4xx codes (400 validation, 403 not-the-parent),
// and the base axios instance has no response interceptor, so those reject.
// Surface the backend's actionable `description` when present, else a
// status-appropriate fallback.
const friendlyChildError = (err: unknown): string => {
  const axiosErr = err as AxiosError<{ description?: string }>
  const description = axiosErr?.response?.data?.description
  if (description) return description
  const status = axiosErr?.response?.status
  if (status === 403) {
    return 'You are not allowed to add a child to this account.'
  }
  if (status === 400) {
    return 'Could not add child. Please check the details and try again.'
  }
  return 'Could not add child. Please try again.'
}

export class UserService {
  private url = '/users'

  updateProfile = async (
    fields: UpdateProfileRequest
  ): Promise<UpdateProfileResponse> => {
    const { userId, first_name, last_name } = fields
    const { data } = await axios.put<UpdateProfileResponse>(
      `${this.url}/${userId}`,
      { first_name, last_name }
    )
    if (data.error) {
      handleError(
        data.description
          ? Error(data.description)
          : Error('Error updating profile. Please try again.')
      )
    }
    return data
  }

  changePassword = async (
    fields: ChangePasswordRequest
  ): Promise<{ error: boolean; description?: string }> => {
    const { userId, current_password, new_password } = fields
    const { data } = await axios.put<{ error: boolean; description?: string }>(
      `${this.url}/${userId}/password`,
      { current_password, new_password }
    )
    if (data.error) {
      handleError(
        data.description
          ? Error(data.description)
          : Error('Error changing password. Please try again.')
      )
    }
    return data
  }

  setUserFamily = async (
    fields: UserFamilyRequest
  ): Promise<UserProfile | undefined> => {
    const { userId, ...body } = fields
    const { data } = await axios.post<{
      data: UserProfile
      error: boolean
      description: string
    }>(`${this.url}/${userId}/update`, body)
    if (data.error) {
      handleError(Error(data.description))
    } else {
      return data.data
    }
  }

  removeUserFamily = async ({
    userId,
    memberId,
  }: RemoveUserFamilyRequest): Promise<UserProfile | undefined> => {
    // DELETE /users/<user_id>/family/<member_id> (acespace-backend#137).
    // Staff-guarded on the path user; returns the updated path user.
    const { data } = await axios.delete<{
      data: UserProfile
      error: boolean
      description: string
    }>(`${this.url}/${userId}/family/${memberId}`)
    if (data.error) {
      handleError(Error(data.description))
    } else {
      return data.data
    }
  }

  createChild = async ({
    parentUserId,
    name,
    last_name,
    dob,
    age_group,
  }: CreateChildRequest): Promise<CreateChildResponse> => {
    // POST /users/<parent_user_id>/children (product#379). Creates a
    // login-less managed dependent with a server-generated plus-addressed
    // email and auto-links it to the parent. Parent-only (403 otherwise).
    try {
      const { data } = await axios.post<CreateChildResponse>(
        `${this.url}/${parentUserId}/children`,
        { name, last_name, dob, age_group }
      )
      // Some endpoints still return a 200 error-envelope; honor it.
      if (data.error) {
        handleError(Error(data.description || 'Could not add child.'))
      }
      return data
    } catch (err) {
      const message = friendlyChildError(err)
      handleError(Error(message))
      return { error: true, description: message }
    }
  }

  getUserAppointments = async (userId: number): Promise<Appointment[]> => {
    // Fetch up to the backend safety cap (1000, set in acespace-backend PR
    // #119) and paginate client-side. Server-side pagination isn't viable
    // here yet — the endpoint sorts by created_at asc and doesn't split
    // past/upcoming, which the tab's segmented UI both need.
    const { data } = await axios.get<{ data: Appointment[]; error: boolean }>(
      `${this.url}/${userId}?appointments&page=0&limit=1000`
    )
    if (data.error) {
      handleError(Error('Error fetching users appointments'))
      return []
    } else {
      return data.data
    }
  }

  getChildren = async (parentUserId: number): Promise<ChildSummary[]> => {
    // product#581 (be#201): GET /users/<parentId>/children → {id,name,last_name,
    // title,email}. Richer than is_profile's children[{id,title}]; used by the
    // management UI and the family filters. Self/parent/staff/admin-scoped.
    const { data } = await axios.get<GetChildrenResponse>(
      `${this.url}/${parentUserId}/children`
    )
    if (data.error) {
      handleError(Error('Error fetching children'))
      return []
    }
    return data.data ?? []
  }

  editChild = async ({
    childId,
    name,
    last_name,
    email,
    password,
  }: EditChildRequest): Promise<{ error: boolean | string; description?: string }> => {
    // product#581: name/last_name/email are QUERY-STRING params on
    // POST /users/<childId>/update, but the `password` goes in the JSON BODY —
    // NOT the query string: a credential in the URL gets logged everywhere
    // (CWE-598), so the QA security review moved it to request.json. The
    // password is already AES-encrypted by the caller (useEncrypt, same contract
    // as /signup); the backend decrypts. Setting a real email + password is how
    // a child gains an independent login (needed before spinning off a
    // standalone account).
    const params = new URLSearchParams()
    if (name !== undefined) params.set('name', name)
    if (last_name !== undefined) params.set('last_name', last_name)
    if (email !== undefined) params.set('email', email)
    const body = password !== undefined ? { password } : {}
    const { data } = await axios.post<{
      error: boolean | string
      description?: string
    }>(`${this.url}/${childId}/update?${params.toString()}`, body)
    if (data.error) {
      handleError(Error(data.description || 'Could not update child.'))
    }
    return data
  }

  getFamilyMemberAppointments = async (
    userId: number
  ): Promise<Appointment[]> => {
    // product#581: a family member's appointments via the dedicated
    // /users/<id>/appointments route (the one the ancestry case permits for a
    // parent — NOT the is_profile ?appointments form). Used by the family
    // filter; VIEW-only (no edit/cancel controls rendered on child rows).
    const { data } = await axios.get<{ data: Appointment[]; error: boolean }>(
      `${this.url}/${userId}/appointments?page=0&limit=1000`
    )
    if (data.error) {
      handleError(Error('Error fetching appointments'))
      return []
    }
    return data.data ?? []
  }

  getUserProfile = async (userId: number): Promise<UserProfile> => {
    const { data } = await axios.get<{ data: UserProfile }>(
      `${this.url}/${userId}?is_profile=true`
    )
    if (data.data.error) {
      handleError(Error('Error fetching user profile'))
      return {} as UserProfile
    } else {
      return data.data
    }
  }

  getUserTransactions = async (
    userId: number,
    page = 0,
    limit = 10
  ): Promise<{ data: UserPastPurchase[]; totalCount: number }> => {
    try {
      const { data } = await axios.get<{
        data: UserPastPurchase[]
        description?: { total_count?: number }
        error: boolean | string
      }>(`${this.url}/${userId}/transactions?page=${page}&limit=${limit}`)
      if (data.error) {
        return { data: [], totalCount: 0 }
      }
      return {
        data: data.data ?? [],
        totalCount: data.description?.total_count ?? (data.data?.length ?? 0),
      }
    } catch {
      return { data: [], totalCount: 0 }
    }
  }

  resendReceipt = async (
    transactionId: number
  ): Promise<{ error: boolean; description?: string }> => {
    const { data } = await axios.post<{
      error: boolean
      description?: string
    }>(`/transactions/${transactionId}/resend-receipt`)
    if (data.error) {
      handleError(
        data.description
          ? Error(data.description)
          : Error('Could not resend receipt. Please try again.')
      )
    }
    return data
  }

  getUserTransactionsByLocationId = async ({
    locationId,
    userId,
  }: UserTransactionsByLocationRequest): Promise<UserTransactionsByLocationResponse> => {
    const { data } = await axios.get<UserTransactionsByLocationResponse>(
      `locations/${locationId}/invoices/${userId}`
    )
    if (data.error) {
      handleError(
        data.description
          ? Error(data.description)
          : Error('Error fetching location')
      )
      return {} as UserTransactionsByLocationResponse
    } else {
      return data
    }
  }

  getPaymentConfig = async (
    locationId: string | number
  ): Promise<string | null> => {
    const { data } = await axios.get<{
      data: { public_api_key: string | null }
      error: boolean
      description?: string
    }>(`/locations/${locationId}/payment_config`)
    if (data.error) {
      handleError(
        data.description
          ? Error(data.description)
          : Error('Error fetching payment config')
      )
      return null
    }
    return data.data.public_api_key
  }

  // Per-location inbound processor (payment_processor_inbound,
  // acespace-backend#123), exposed via the payment_config endpoint. Used to
  // decide whether to render the DishOut hosted-form embed vs the existing
  // Chosen Payments / USAePay form. Defaults to null (existing path) on
  // absence or error so non-DishOut locations are unaffected.
  getPaymentProcessor = async (
    locationId: string | number
  ): Promise<string | null> => {
    try {
      const { data } = await axios.get<{
        data?: { payment_processor_inbound?: string; processor?: string }
        error?: boolean
      }>(`/locations/${locationId}/payment_config`)
      if (data.error) return null
      return (
        data.data?.payment_processor_inbound ?? data.data?.processor ?? null
      )
    } catch {
      return null
    }
  }

  // POST /users/<user_id>/dishout/save_card/<user_id> (acespace-backend#123,
  // Phase 5). Returns the EPay hosted-form HTML embed + a state JWT. Returns
  // null on error (the caller shows a friendly message); the HTML is injected
  // into an isolated iframe so card data never touches our JS.
  getDishoutSaveCardForm = async (
    userId: number
  ): Promise<DishoutSaveCardResponse | null> => {
    try {
      const { data } = await axios.post<RawDishoutSaveCard>(
        `/users/${userId}/dishout/save_card/${userId}`
      )
      if (data.error) {
        handleError(
          Error(data.description || 'Could not start DishOut card setup.')
        )
        return null
      }
      const env = data.data ?? data
      const html = env.html ?? env.form_html ?? env.embed
      if (!html) {
        handleError(Error('DishOut card form unavailable. Please try again.'))
        return null
      }
      return { html, state_jwt: env.state_jwt }
    } catch {
      handleError(
        Error('Could not load the DishOut card form. Please try again.')
      )
      return null
    }
  }

  savePaymentMethod = async (
    locationId: string | number,
    userId: number,
    paymentKey: string
  ): Promise<boolean> => {
    const { data } = await axios.post<{ error: boolean; description?: string }>(
      `/locations/${locationId}/save_card/${userId}`,
      { chosen_payments_token: paymentKey }
    )
    if (data.error) {
      handleError(
        data.description
          ? Error(data.description)
          : Error('Error saving payment method')
      )
      return false
    }
    return true
  }

  deletePaymentMethod = async (userId: number, paymentMethodId: number): Promise<boolean> => {
    const { data } = await axios.delete<{ error: boolean; description?: string }>(
      `/users/${userId}/payment_methods/${paymentMethodId}`
    )
    if (data.error) {
      handleError(
        data.description
          ? Error(data.description)
          : Error('Error deleting payment method')
      )
      return false
    }
    return true
  }

  getUserBalance = async ({
    locationId,
    userId,
  }: UserBalanceRequest): Promise<UserBalanceResponse> => {
    const { data } = await axios.get<UserBalanceResponse>(
      `/locations/${locationId}/balances/${userId}`
    )
    if (data.error) {
      handleError(
        data.description
          ? Error(data.description)
          : Error('Error fetching user balance')
      )
      return {} as UserBalanceResponse
    } else {
      return data
    }
  }
}

export default new UserService()
